import { Plus, ShieldCheck, Tag } from 'lucide-react'
import { Link } from 'react-router-dom'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Badge, Section, WorkspaceSkeleton } from '../components/UI'
import { ActionToast } from '../components/ActionToast'
import { useTeamScope } from '../components/TeamScope'
import { supabase } from '../lib/supabase'

type BenchmarkProfile = {
  id: string
  organisation_id: string
  team_id: string | null
  category_id: string | null
  complexity: 'simple' | 'standard' | 'complex' | 'high_complexity' | null
  name: string
  description: string | null
  is_default: boolean
  is_active: boolean
}

type BenchmarkRule = {
  benchmark_profile_id: string
  metric_key: string
  target_seconds: number
  warning_seconds: number | null
  direction: 'max' | 'min'
}

type Team = {
  id: string
  name: string
}

type WorkCategory = {
  id: string
  team_id: string | null
  name: string
  description: string | null
  is_active: boolean
}

type QualityProfile = {
  id: string
  team_id: string | null
  category_id: string | null
  complexity: 'simple' | 'standard' | 'complex' | 'high_complexity' | null
  name: string
  description: string | null
  minimum_pass_percent: number
  is_default: boolean
  is_active: boolean
}

type QualityRequirement = {
  id: string
  quality_profile_id: string
  requirement_key: string
  label: string
  description: string | null
  weight: number
  is_required: boolean
  display_order: number
}

const metricLabels: Record<string, string> = {
  arrival_to_reply_sent_seconds: 'Response time'
}

function formatSeconds(value: number | null | undefined) {
  if (value == null) return 'Not set'
  if (value < 60) return `${value}s`
  const minutes = Math.round(value / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`
}

function minutesToSeconds(value: string) {
  const minutes = Number(value)
  if (!Number.isFinite(minutes) || minutes < 0) return null
  return Math.round(minutes * 60)
}

function complexityLabel(value: BenchmarkProfile['complexity'] | QualityProfile['complexity']) {
  if (!value) return 'All complexity levels'
  return value.replace(/_/g, ' ')
}

export function Benchmarks() {
  const { selectedTeamId } = useTeamScope()
  const [profiles, setProfiles] = useState<BenchmarkProfile[]>([])
  const [rules, setRules] = useState<BenchmarkRule[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [categories, setCategories] = useState<WorkCategory[]>([])
  const [qualityProfiles, setQualityProfiles] = useState<QualityProfile[]>([])
  const [qualityRequirements, setQualityRequirements] = useState<QualityRequirement[]>([])
  const [role, setRole] = useState('')

  const [showCreate, setShowCreate] = useState(false)
  const [showCategoryCreate, setShowCategoryCreate] = useState(false)
  const [showQualityCreate, setShowQualityCreate] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingCategory, setSavingCategory] = useState(false)
  const [savingQuality, setSavingQuality] = useState(false)

  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)

  const [name, setName] = useState('Organisation standard')
  const [teamId, setTeamId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [complexity, setComplexity] = useState('')
  const [responseMinutes, setResponseMinutes] = useState('60')

  const [categoryName, setCategoryName] = useState('')
  const [categoryDescription, setCategoryDescription] = useState('')
  const [categoryTeamId, setCategoryTeamId] = useState('')

  const [qualityName, setQualityName] = useState('Response quality standard')
  const [qualityTeamId, setQualityTeamId] = useState('')
  const [qualityCategoryId, setQualityCategoryId] = useState('')
  const [qualityComplexity, setQualityComplexity] = useState('')
  const [qualityPassPercent, setQualityPassPercent] = useState('80')

  async function load() {
    if (!supabase) { setLoading(false); return }
    setLoading(true)

    setError('')

    const { data: authData, error: authError } = await supabase.auth.getUser()
    if (authError || !authData.user) {
      setError('You are not signed in.')
      setLoading(false)
      return
    }

    const membershipResult = await supabase
      .from('memberships')
      .select('organisation_id,role')
      .eq('user_id', authData.user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle()

    if (membershipResult.error) {
      setError(membershipResult.error.message)
      setLoading(false)
      return
    }

    if (!membershipResult.data?.organisation_id) {
      setError('No organisation membership was found.')
      setLoading(false)
      return
    }

    setRole(membershipResult.data.role)
    const organisationId = membershipResult.data.organisation_id

    const [
      profilesResult,
      teamsResult,
      categoriesResult,
      qualityProfilesResult
    ] = await Promise.all([
      supabase
        .from('benchmark_profiles')
        .select('id,organisation_id,team_id,category_id,complexity,name,description,is_default,is_active')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: true }),

      supabase
        .from('teams')
        .select('id,name')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: true }),

      supabase
        .from('work_categories')
        .select('id,team_id,name,description,is_active')
        .eq('organisation_id', organisationId)
        .eq('is_active', true)
        .order('name', { ascending: true }),

      supabase
        .from('quality_profiles')
        .select('id,team_id,category_id,complexity,name,description,minimum_pass_percent,is_default,is_active')
        .eq('organisation_id', organisationId)
        .eq('is_active', true)
        .order('created_at', { ascending: true })
    ])

    if (profilesResult.error) { setError(profilesResult.error.message); setLoading(false); return }
    if (teamsResult.error) { setError(teamsResult.error.message); setLoading(false); return }
    if (categoriesResult.error) { setError(categoriesResult.error.message); setLoading(false); return }
    if (qualityProfilesResult.error) { setError(qualityProfilesResult.error.message); setLoading(false); return }

    const loadedProfiles = (profilesResult.data ?? []) as BenchmarkProfile[]
    const loadedQualityProfiles = (qualityProfilesResult.data ?? []) as QualityProfile[]

    setProfiles(loadedProfiles)
    setTeams((teamsResult.data ?? []) as Team[])
    setCategories((categoriesResult.data ?? []) as WorkCategory[])
    setQualityProfiles(loadedQualityProfiles)

    if (loadedProfiles.length) {
      const rulesResult = await supabase
        .from('benchmark_rules')
        .select('benchmark_profile_id,metric_key,target_seconds,warning_seconds,direction')
        .in('benchmark_profile_id', loadedProfiles.map(profile => profile.id))

      if (rulesResult.error) { setError(rulesResult.error.message); setLoading(false); return }
      setRules((rulesResult.data ?? []) as BenchmarkRule[])
    } else {
      setRules([])
    }

    if (loadedQualityProfiles.length) {
      const qualityRequirementsResult = await supabase
        .from('quality_requirements')
        .select('id,quality_profile_id,requirement_key,label,description,weight,is_required,display_order')
        .in('quality_profile_id', loadedQualityProfiles.map(profile => profile.id))
        .order('display_order', { ascending: true })

      if (qualityRequirementsResult.error) { setError(qualityRequirementsResult.error.message); setLoading(false); return }
      setQualityRequirements((qualityRequirementsResult.data ?? []) as QualityRequirement[])
    } else {
      setQualityRequirements([])
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    setTeamId(selectedTeamId ?? '')
    setCategoryTeamId(selectedTeamId ?? '')
    setQualityTeamId(selectedTeamId ?? '')
  }, [selectedTeamId])

  const rulesByProfile = useMemo(() => {
    const map = new Map<string, BenchmarkRule[]>()
    for (const rule of rules) {
      const list = map.get(rule.benchmark_profile_id) ?? []
      list.push(rule)
      map.set(rule.benchmark_profile_id, list)
    }
    return map
  }, [rules])


  const visibleCategories = useMemo(
    () => categories.filter(category => !selectedTeamId || category.team_id == null || category.team_id === selectedTeamId),
    [categories, selectedTeamId]
  )

  const visibleProfiles = useMemo(
    () => profiles.filter(profile => !selectedTeamId || profile.team_id == null || profile.team_id === selectedTeamId),
    [profiles, selectedTeamId]
  )

  const visibleQualityProfiles = useMemo(
    () => qualityProfiles.filter(profile => !selectedTeamId || profile.team_id == null || profile.team_id === selectedTeamId),
    [qualityProfiles, selectedTeamId]
  )

  const qualityRequirementsByProfile = useMemo(() => {
    const map = new Map<string, QualityRequirement[]>()
    for (const requirement of qualityRequirements) {
      const list = map.get(requirement.quality_profile_id) ?? []
      list.push(requirement)
      map.set(requirement.quality_profile_id, list)
    }
    return map
  }, [qualityRequirements])

  function ruleValue(profileId: string, metricKey: string) {
    return rulesByProfile.get(profileId)?.find(rule => rule.metric_key === metricKey)?.target_seconds ?? null
  }

  function teamNameFromId(id: string | null) {
    if (!id) return 'Organisation-wide'
    return teams.find(team => team.id === id)?.name ?? 'Team'
  }

  function categoryNameFromId(id: string | null) {
    if (!id) return 'All categories'
    return categories.find(category => category.id === id)?.name ?? 'Category'
  }

  async function getOrganisationId() {
    if (!supabase) throw new Error('Supabase is not configured.')

    const { data: authData, error: authError } = await supabase.auth.getUser()
    if (authError || !authData.user) throw new Error('You are not signed in.')

    const { data: membership, error: membershipError } = await supabase
      .from('memberships')
      .select('organisation_id')
      .eq('user_id', authData.user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle()

    if (membershipError) throw membershipError
    if (!membership?.organisation_id) throw new Error('No organisation membership was found.')

    return membership.organisation_id as string
  }

  async function createCategory(event: FormEvent) {
    event.preventDefault()
    if (!supabase || savingCategory) return

    setSavingCategory(true)
    setError('')
    setMessage('')

    try {
      const organisationId = await getOrganisationId()

      const { error: insertError } = await supabase
        .from('work_categories')
        .insert({
          organisation_id: organisationId,
          team_id: categoryTeamId || null,
          name: categoryName.trim(),
          description: categoryDescription.trim() || null,
          is_active: true
        })

      if (insertError) throw insertError

      setCategoryName('')
      setCategoryDescription('')
      setCategoryTeamId('')
      setShowCategoryCreate(false)
      setMessage('Work category created.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create work category.')
    } finally {
      setSavingCategory(false)
    }
  }

  async function createBenchmark(event: FormEvent) {
    event.preventDefault()
    if (!supabase || saving) return

    setSaving(true)
    setError('')
    setMessage('')

    try {
      const organisationId = await getOrganisationId()

      const targets = [
        ['arrival_to_reply_sent_seconds', responseMinutes]
      ] as const

      for (const [, value] of targets) {
        if (minutesToSeconds(value) == null) {
          throw new Error('All SLA targets must be valid numbers of minutes.')
        }
      }

      const isOrganisationWide = !teamId && !categoryId && !complexity
      const isDefault = isOrganisationWide && !profiles.some(profile => profile.is_default && profile.is_active)

      const { data: profile, error: profileError } = await supabase
        .from('benchmark_profiles')
        .insert({
          organisation_id: organisationId,
          team_id: teamId || null,
          category_id: categoryId || null,
          complexity: complexity || null,
          name: name.trim() || 'Operating standard',
          description: 'Mail Percent SLA target.',
          is_default: isDefault,
          is_active: true
        })
        .select('id')
        .single()

      if (profileError) throw profileError

      const { error: rulesError } = await supabase
        .from('benchmark_rules')
        .insert(
          targets.map(([metricKey, value]) => ({
            benchmark_profile_id: profile.id,
            metric_key: metricKey,
            target_seconds: minutesToSeconds(value),
            direction: 'max'
          }))
        )

      if (rulesError) throw rulesError

      setMessage('SLA target created successfully.')
      setShowCreate(false)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create SLA target.')
    } finally {
      setSaving(false)
    }
  }

  async function createQualityStandard(event: FormEvent) {
    event.preventDefault()
    if (!supabase || savingQuality) return

    setSavingQuality(true)
    setError('')
    setMessage('')

    try {
      const organisationId = await getOrganisationId()
      const passPercent = Number(qualityPassPercent)

      if (!Number.isFinite(passPercent) || passPercent < 0 || passPercent > 100) {
        throw new Error('Minimum pass percentage must be between 0 and 100.')
      }

      const isOrganisationWide = !qualityTeamId && !qualityCategoryId && !qualityComplexity
      const isDefault = isOrganisationWide && !qualityProfiles.some(profile => profile.is_default && profile.is_active)

      const { data: profile, error: profileError } = await supabase
        .from('quality_profiles')
        .insert({
          organisation_id: organisationId,
          team_id: qualityTeamId || null,
          category_id: qualityCategoryId || null,
          complexity: qualityComplexity || null,
          name: qualityName.trim() || 'Response quality standard',
          description: 'Organisation-defined response quality standard.',
          minimum_pass_percent: passPercent,
          is_default: isDefault,
          is_active: true
        })
        .select('id')
        .single()

      if (profileError) throw profileError

      const starterRequirements = [
        {
          quality_profile_id: profile.id,
          requirement_key: 'clarity',
          label: 'Clear and understandable',
          description: 'The response communicates the answer or next step clearly.',
          weight: 1,
          is_required: true,
          display_order: 1
        },
        {
          quality_profile_id: profile.id,
          requirement_key: 'completeness',
          label: 'Complete response',
          description: 'The response addresses the material points required for the case.',
          weight: 1,
          is_required: true,
          display_order: 2
        },
        {
          quality_profile_id: profile.id,
          requirement_key: 'tone',
          label: 'Appropriate tone',
          description: 'The response follows the organisation’s expected communication standard.',
          weight: 1,
          is_required: true,
          display_order: 3
        },
        {
          quality_profile_id: profile.id,
          requirement_key: 'required_action',
          label: 'Required action completed',
          description: 'Any action or next-step requirement for the category was handled or clearly communicated.',
          weight: 1,
          is_required: true,
          display_order: 4
        }
      ]

      const { error: requirementsError } = await supabase
        .from('quality_requirements')
        .insert(starterRequirements)

      if (requirementsError) throw requirementsError

      setShowQualityCreate(false)
      setQualityName('Response quality standard')
      setQualityTeamId('')
      setQualityCategoryId('')
      setQualityComplexity('')
      setQualityPassPercent('80')
      setMessage('Quality standard created.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create quality standard.')
    } finally {
      setSavingQuality(false)
    }
  }

  const canManage = role === 'owner' || role === 'admin'

  if (loading) return <WorkspaceSkeleton rows={4}/>

  return (
    <div className="stack-lg">
      <div className="notice">
        <ShieldCheck size={20}/>
        <div>
          <strong>SLA targets are never silently rewritten.</strong>
          <span>
            Mail Percent compares measured work with the organisation’s chosen SLA and quality targets while keeping operating pressure visible separately.
          </span>
        </div>
      </div>

      {error && (
        <div className="notice">
          <div>
            <strong>SLA Performance data unavailable</strong>
            <span>{error}</span>
          </div>
        </div>
      )}

      {message && <ActionToast title="Updated" message={message} onDismiss={() => setMessage('')}/>}

      <Section
        title="Work categories"
        subtitle={`${visibleCategories.length} active categor${visibleCategories.length === 1 ? 'y' : 'ies'}`}
        action={
          canManage
            ? <button className="button secondary" onClick={() => setShowCategoryCreate(value => !value)}>
                <Tag size={16}/>
                {showCategoryCreate ? 'Close' : 'New category'}
              </button>
            : undefined
        }
      >
        {showCategoryCreate && canManage && (
          <form className="benchmark-editor creation-panel category-creation-panel" onSubmit={createCategory}>
            <div className="creation-panel-heading">
              <div>
                <span className="eyebrow">New work category</span>
                <h3>Create a category</h3>
                <p>Group similar customer emails so the right SLA target can be applied.</p>
              </div>
            </div>
            <div className="field">
              <label htmlFor="category-name">Category name</label>
              <input
                id="category-name"
                value={categoryName}
                onChange={event => setCategoryName(event.target.value)}
                required
                placeholder="Billing"
              />
            </div>

            <div className="field">
              <label htmlFor="category-team">Applies to</label>
              <select
                id="category-team"
                value={categoryTeamId}
                onChange={event => setCategoryTeamId(event.target.value)}
              >
                <option value="">Whole organisation</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="category-description">Description</label>
              <input
                id="category-description"
                value={categoryDescription}
                onChange={event => setCategoryDescription(event.target.value)}
                placeholder="Billing questions and payment-related issues"
              />
            </div>

            <div className="creation-panel-actions">
              <button type="button" className="button secondary" onClick={() => setShowCategoryCreate(false)}>Cancel</button>
              <button type="submit" className="button primary" disabled={savingCategory}>
                <Plus size={16}/>
                {savingCategory ? 'Creating…' : 'Create category'}
              </button>
            </div>
          </form>
        )}

        <div className="report-insight-grid">
          {visibleCategories.map(category => (
            <div key={category.id}>
              <span>{teamNameFromId(category.team_id)}</span>
              <strong>{category.name}</strong>
              <small>{category.description || 'No description'}</small>
            </div>
          ))}

          {visibleCategories.length === 0 && (
            <p className="muted">No work categories have been created yet.</p>
          )}
        </div>
      </Section>

      <Section
        title="SLA targets"
        subtitle={`${visibleProfiles.filter(profile => profile.is_active).length} active SLA target${visibleProfiles.filter(profile => profile.is_active).length === 1 ? '' : 's'}`}
        action={
          canManage
            ? <button className="button primary" onClick={() => setShowCreate(value => !value)}>
                <Plus size={16}/>
                New SLA target
              </button>
            : undefined
        }
      >
        {showCreate && canManage && (
          <form className="benchmark-editor creation-panel benchmark-creation-panel" onSubmit={createBenchmark}>
            <div className="creation-panel-heading">
              <div>
                <span className="eyebrow">New SLA target</span>
                <h3>Create an SLA target</h3>
                <p>Choose who this SLA applies to, then define the response times your organisation commits to measuring.</p>
              </div>
            </div>
            <div className="field">
              <label htmlFor="benchmark-name">SLA name</label>
              <input
                id="benchmark-name"
                value={name}
                onChange={event => setName(event.target.value)}
                placeholder="Billing standard"
              />
            </div>

            <div className="field">
              <label htmlFor="benchmark-team">Team</label>
              <select id="benchmark-team" value={teamId} onChange={event => setTeamId(event.target.value)}>
                <option value="">Any team</option>
                {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
            </div>

            <div className="field">
              <label htmlFor="benchmark-category">Category</label>
              <select id="benchmark-category" value={categoryId} onChange={event => setCategoryId(event.target.value)}>
                <option value="">Any category</option>
                {categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
            </div>

            <div className="field">
              <label htmlFor="benchmark-complexity">Complexity</label>
              <select id="benchmark-complexity" value={complexity} onChange={event => setComplexity(event.target.value)}>
                <option value="">Any complexity</option>
                <option value="simple">Simple</option>
                <option value="standard">Standard</option>
                <option value="complex">Complex</option>
                <option value="high_complexity">High complexity</option>
              </select>
            </div>

            <div className="benchmark-editor-grid">
              <div className="field">
                <label>First response SLA (minutes)</label>
                <input type="number" min="0" step="0.5" value={responseMinutes} onChange={event => setResponseMinutes(event.target.value)}/>
                <small className="muted">Measured from the customer's email arriving to the first reply sent by your organisation.</small>
              </div>
            </div>

            <div className="creation-panel-actions">
              <button type="button" className="button secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" className="button primary" disabled={saving}>
                {saving ? 'Saving…' : 'Create SLA target'}
              </button>
            </div>
          </form>
        )}

        <div className="split-card-grid benchmark-split-grid">
          {visibleProfiles.filter(profile => profile.is_active).map(profile => (
            <Link
              to={`/app/sla-performance/${profile.id}`}
              className="split-reveal-card benchmark-split-card"
              key={profile.id}
              aria-label={`Open ${profile.name} SLA target`}
            >
              <div className="split-reveal-identity">
                <div>
                  <span className="eyebrow">{teamNameFromId(profile.team_id)}</span>
                  <strong>{profile.name}</strong>
                  <span>{categoryNameFromId(profile.category_id)} · {complexityLabel(profile.complexity)}</span>
                </div>

                <Badge tone={profile.is_default ? 'blue' : 'neutral'}>
                  {profile.is_default ? 'Default' : 'Active'}
                </Badge>
              </div>

              <div className="split-reveal-stats">
                <div className="split-stat-reveal benchmark-stat-reveal">
                  <div>
                    <span>{metricLabels.arrival_to_reply_sent_seconds}</span>
                    <strong>≤ {formatSeconds(ruleValue(profile.id, 'arrival_to_reply_sent_seconds'))}</strong>
                  </div>
                </div>

                <div className="split-reveal-foot">
                  <span>Open full SLA target</span>
                </div>
              </div>
            </Link>
          ))}

          {visibleProfiles.filter(profile => profile.is_active).length === 0 && (
            <p className="muted">No SLA target has been created yet.</p>
          )}
        </div>
      </Section>

      <Section
        title="Quality standards"
        subtitle={`${visibleQualityProfiles.length} active response quality standard${visibleQualityProfiles.length === 1 ? '' : 's'}`}
        action={
          canManage
            ? <button className="button primary" onClick={() => setShowQualityCreate(value => !value)}>
                <Plus size={16}/>
                New quality standard
              </button>
            : undefined
        }
      >
        {showQualityCreate && canManage && (
          <form className="benchmark-editor creation-panel quality-creation-panel" onSubmit={createQualityStandard}>
            <div className="creation-panel-heading">
              <div>
                <span className="eyebrow">New response standard</span>
                <h3>Create a quality standard</h3>
                <p>Set the minimum quality level and choose which customer-email work it should apply to.</p>
              </div>
            </div>
            <div className="field">
              <label htmlFor="quality-name">Standard name</label>
              <input
                id="quality-name"
                value={qualityName}
                onChange={event => setQualityName(event.target.value)}
                placeholder="General enquiry quality standard"
              />
            </div>

            <div className="field">
              <label htmlFor="quality-team">Team</label>
              <select id="quality-team" value={qualityTeamId} onChange={event => setQualityTeamId(event.target.value)}>
                <option value="">Any team</option>
                {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
            </div>

            <div className="field">
              <label htmlFor="quality-category">Category</label>
              <select id="quality-category" value={qualityCategoryId} onChange={event => setQualityCategoryId(event.target.value)}>
                <option value="">Any category</option>
                {categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
            </div>

            <div className="field">
              <label htmlFor="quality-complexity">Complexity</label>
              <select id="quality-complexity" value={qualityComplexity} onChange={event => setQualityComplexity(event.target.value)}>
                <option value="">Any complexity</option>
                <option value="simple">Simple</option>
                <option value="standard">Standard</option>
                <option value="complex">Complex</option>
                <option value="high_complexity">High complexity</option>
              </select>
            </div>

            <div className="field">
              <label htmlFor="quality-pass">Minimum pass percentage</label>
              <input
                id="quality-pass"
                type="number"
                min="0"
                max="100"
                step="1"
                value={qualityPassPercent}
                onChange={event => setQualityPassPercent(event.target.value)}
              />
            </div>

            <div className="notice">
              <div>
                <strong>Starter requirements</strong>
                <span>
                  The standard starts with four editable requirement records: clarity, completeness, appropriate tone and required action completed.
                </span>
              </div>
            </div>

            <div className="creation-panel-actions">
              <button type="button" className="button secondary" onClick={() => setShowQualityCreate(false)}>Cancel</button>
              <button type="submit" className="button primary" disabled={savingQuality}>
                {savingQuality ? 'Saving…' : 'Create quality standard'}
              </button>
            </div>
          </form>
        )}

        <div className="report-insight-grid">
          {visibleQualityProfiles.map(profile => {
            const requirements = qualityRequirementsByProfile.get(profile.id) ?? []

            return (
              <div key={profile.id}>
                <span>{teamNameFromId(profile.team_id)}</span>
                <strong>{profile.name}</strong>
                <small>
                  {categoryNameFromId(profile.category_id)} · {complexityLabel(profile.complexity)}
                </small>
                <small>
                  Pass at {Math.round(Number(profile.minimum_pass_percent))}% · {requirements.length} requirement{requirements.length === 1 ? '' : 's'}
                </small>
              </div>
            )
          })}

          {visibleQualityProfiles.length === 0 && (
            <p className="muted">
              No response quality standard has been created yet.
            </p>
          )}
        </div>
      </Section>
    </div>
  )
}
